/**
 * Design Tokens - Single Source of Truth for Design System
 *
 * All colors, spacing, typography, and other design values should
 * be imported from this file to ensure consistency across the app.
 */

// ===== COLORS =====

export const colors = {
  // Primary brand colors
  primary: '#000000',      // Black
  secondary: '#FFFFFF',    // White
  accent: '#FFC65D',       // Yellow (brand color)

  // Background colors
  bg: {
    primary: '#FFFFFF',    // Main backgrounds (cards, pages)
    secondary: '#f5f5f5',  // Secondary backgrounds, hover states
    tertiary: '#e0e0e0',   // Disabled backgrounds
    hover: '#e0e0e0',      // Hover backgrounds
  },

  // Border colors
  border: {
    default: '#e0e0e0',    // Default borders
    focus: '#FFC65D',      // Focus state borders
    error: '#e74c3c',      // Error state borders
  },

  // Text colors
  text: {
    primary: '#000000',    // Primary text
    secondary: '#666666',  // Secondary text, labels
    tertiary: '#999999',   // Tertiary text, placeholders
    disabled: '#cccccc',   // Disabled text
    inverse: '#FFFFFF',    // Text on dark backgrounds
  },

  // Status colors
  status: {
    success: '#27ae60',    // Success states
    warning: '#f39c12',    // Warning states
    error: '#e74c3c',      // Error states, critical
    info: '#3498db',       // Info states
  },

  // Severity levels (for badges, alerts)
  severity: {
    critical: '#e74c3c',
    high: '#e67e22',
    medium: '#f39c12',
    low: '#95a5a6',
    ok: '#27ae60',
  },

  // Badge variants
  badge: {
    default: '#f5f5f5',
    success: '#FFC65D',
    warning: '#FFC65D',
    danger: '#FFFFFF',
    info: '#e3f2fd',
    gray: '#f5f5f5',
  },

  // Button variants
  button: {
    primary: {
      bg: '#FFC65D',
      text: '#000000',
      hover: '#ffb84d',
    },
    secondary: {
      bg: '#f5f5f5',
      text: '#000000',
      hover: '#e0e0e0',
    },
    danger: {
      bg: '#FFFFFF',
      text: '#000000',
      hover: 'rgba(255, 255, 255, 0.9)',
    },
    success: {
      bg: '#FFC65D',
      text: '#000000',
      hover: '#ffb84d',
    },
    ghost: {
      bg: 'transparent',
      text: '#000000',
      hover: '#f5f5f5',
    },
  },
};

// ===== SPACING =====

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  xxxl: '48px',
};

// ===== TYPOGRAPHY =====

export const typography = {
  fontSize: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
  },

  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// ===== BORDER RADIUS =====

export const borderRadius = {
  sm: '4px',   // Badges, small elements
  md: '6px',   // Buttons, inputs
  lg: '8px',   // Cards, large containers
  xl: '12px',  // Modals, special containers
  round: '50%', // Circular elements
};

// ===== SHADOWS =====

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  none: 'none',
};

// ===== TRANSITIONS =====

export const transitions = {
  fast: '0.1s ease',
  normal: '0.2s ease',
  slow: '0.3s ease',
};

// ===== Z-INDEX =====

export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ===== BREAKPOINTS =====

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px',
};

// ===== DEPRECATED COLORS (DO NOT USE) =====

/**
 * These colors are from the old dark theme and should NEVER be used.
 * They are listed here for reference and to help detect legacy code.
 */
export const DEPRECATED_COLORS = {
  // Dark theme backgrounds (DO NOT USE)
  darkBg1: '#0a0a0a',
  darkBg2: '#1a1a1a',
  darkBg3: '#2a2a2a',

  // Warning: If you see these in the codebase, they should be replaced!
};

// ===== HELPER FUNCTIONS =====

/**
 * Get color by severity level
 * @param {string} severity - 'critical' | 'high' | 'medium' | 'low' | 'ok'
 * @returns {string} Color hex code
 */
export const getSeverityColor = (severity) => {
  return colors.severity[severity] || colors.severity.ok;
};

/**
 * Get status color
 * @param {string} status - 'success' | 'warning' | 'error' | 'info'
 * @returns {string} Color hex code
 */
export const getStatusColor = (status) => {
  return colors.status[status] || colors.status.info;
};

/**
 * Check if a color value is deprecated
 * @param {string} color - Color hex code
 * @returns {boolean} True if color is deprecated
 */
export const isDeprecatedColor = (color) => {
  return Object.values(DEPRECATED_COLORS).includes(color);
};

const tokens = {
  colors,
  spacing,
  typography,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  breakpoints,
  getSeverityColor,
  getStatusColor,
  isDeprecatedColor,
};

export default tokens;
